import { Point } from 'src/lib/interfaces/point'
import { Claim } from 'src/lib/interfaces/Claim.interface'
import { Accident } from 'src/lib/interfaces/Accident.interface'
import { ClaimArea } from 'src/lib/interfaces/ClaimArea.interface'
import { AccidentSummary } from 'src/lib/interfaces/AccidentSummary'
import { AccidentType } from 'src/lib/interfaces/AccidentType.interface'
import { CountSummary } from 'src/lib/interfaces/CountSummary.interface'
import { AccidentStatus } from 'src/lib/interfaces/AccidentStatus.interface'
import { AccidentFilterParameters } from 'src/lib/interfaces/AccidentFilterParameters.interface'

import {
  actionTypes,
  ActionTypes,
  AccidentsSetCurrentAction,
  AccidentFilterChangedAction,
  AccidentFetchFilesErrorAction,
  AccidentSummaryFetchSucceeded,
  AccidentFetchFilesSuccessAction,
  AccidentFilterParamsResetAction,
  AccidentHistogramFetchSucceeded,
  AccidentsFetchListSuccessAction,
  AccidentTypesFetchActionSuccess,
  AccidentsFetchClaimSuccessAction,
  AccidentStatusFetchActionSuccess,
  AccidentPostCommentSuccessAction,
  AccidentsSearchClaimsErrorAction,
  AccidentsSetCurrentSuccessAction,
  AccidentsSearchClaimsSuccessAction,
  AccidentsFetchAccidentSuccessAction,
  AccidentsFetchClaimAreasSuccessAction,
  AccidentsFetchOpenClaimsSuccessAction,
  AccidentSetManagerApprovalSuccessAction,
  AccidentPendingStatusCountFetchSucceeded
} from './actions'

const moment = require('moment')

export interface AccidentsState {
  readonly accidents?: Accident[]
  readonly accidentTypes?: AccidentType[]
  readonly accidentStatus?: AccidentStatus[]
  readonly accidentFetchFailed: boolean
  readonly isFetchingSummary: boolean
  readonly isFetchingAccidents: boolean
  readonly isFetchingOpenClaims: boolean
  readonly isFetchingAccidentTypes: boolean
  readonly isFetchingAccidentStatus: boolean
  readonly filterParams: AccidentFilterParameters
  readonly histogramData?: Point[]
  readonly summaryData?: AccidentSummary
  readonly pendingStatusData?: CountSummary
  readonly currentAccident?: Accident
  readonly accidentDetails?: Accident
  readonly claimDetails?: Claim
  readonly claimAreas?: ClaimArea[]
  readonly isFetchingFiles: boolean
  readonly isPostingComment: boolean
  readonly openClaims?: Claim[]
  readonly isUploadingFile: boolean
  readonly isSearchingClaims: boolean
  readonly claimsSearchError?: string
  readonly claimSearchResults?: Claim[]
  readonly isDeletingClaim: boolean
  readonly isDeletingAccident: boolean
  readonly fetchFilesError?: any
}

const get = require('lodash/get')
const sortBy = require('lodash/sortBy')
const orderBy = require('lodash/orderBy')
const findIndex = require('lodash/findIndex')

const today = moment().format('YYYY-MM-DD')
const thisYear = moment().startOf('year').format('YYYY-MM-DD')

const initialFilterParams: AccidentFilterParameters = {
  hideDenied: true,
  lossDateTo: today,
  location: undefined,
  lossDateFrom: thisYear,
  locationPosition: undefined
}

export const initialState: AccidentsState = {
  accidents: undefined,
  claimAreas: undefined,
  openClaims: undefined,
  isFetchingFiles: false,
  isUploadingFile: false,
  isDeletingClaim: false,
  claimDetails: undefined,
  isPostingComment: false,
  accidentTypes: undefined,
  isFetchingSummary: false,
  isSearchingClaims: false,
  accidentStatus: undefined,
  isDeletingAccident: false,
  accidentDetails: undefined,
  currentAccident: undefined,
  accidentFetchFailed: false,
  fetchFilesError: undefined,
  isFetchingAccidents: false,
  isFetchingOpenClaims: false,
  claimsSearchError: undefined,
  isFetchingAccidentTypes: false,
  isFetchingAccidentStatus: false,
  filterParams: initialFilterParams
}

export function reducer(state: AccidentsState = initialState, action: ActionTypes): AccidentsState {
  switch (action.type) {
    case actionTypes.ACCIDENTS_SET_CURRENT:
      return {
        ...state,
        currentAccident: (action as AccidentsSetCurrentAction).payload.accident
      }

    case actionTypes.ACCIDENTS_SET_CURRENT_SUCCESS:
        return {
          ...state,
          currentAccident: (action as AccidentsSetCurrentSuccessAction).payload.accident,
        }

    case actionTypes.ACCIDENTS_FETCH_LIST:
      return {
        ...state,
        accidents: [],
        isFetchingSummary: true,
        isFetchingAccidents: true,
        accidentFetchFailed: false
      }

    case actionTypes.ACCIDENTS_FETCH_LIST_SUCCESS:
      return {
        ...state,
        accidents: (action as AccidentsFetchListSuccessAction).payload.accidents,
        isFetchingAccidents: false,
        accidentFetchFailed: false
      }

    case actionTypes.ACCIDENTS_FETCH_LIST_FAILED:
      return {
        ...state,
        accidents: [],
        isFetchingAccidents: false,
        accidentFetchFailed: true
      }

    // Status
    case actionTypes.ACCIDENT_STATUS_FETCH_LIST:
      return {
        ...state,
        isFetchingAccidentStatus: true
      }

    case actionTypes.ACCIDENT_STATUS_FETCH_LIST_SUCCESS:
      return {
        ...state,
        isFetchingAccidentStatus: false,
        accidentStatus: sortBy((action as AccidentStatusFetchActionSuccess).payload.accidentStatus, 'name')
      }

    case actionTypes.ACCIDENT_STATUS_FETCH_LIST_ERROR:
      return {
        ...state,
        accidentTypes: undefined,
        isFetchingAccidentStatus: false
      }

    // Types
    case actionTypes.ACCIDENT_TYPES_FETCH_LIST:
      return {
        ...state,
        isFetchingAccidentTypes: true
      }

    case actionTypes.ACCIDENT_TYPES_FETCH_LIST_SUCCESS:
      return {
        ...state,
        isFetchingAccidentTypes: false,
        accidentTypes: sortBy((action as AccidentTypesFetchActionSuccess).payload.accidentTypes, 'name')
      }

    case actionTypes.ACCIDENT_TYPES_FETCH_LIST_SUCCESS:
      return {
        ...state,
        isFetchingAccidentTypes: false
      }

    case actionTypes.ACCIDENT_POST_COMMENT:
      return {
        ...state,
        isPostingComment: true
      }

    case actionTypes.ACCIDENT_POST_COMMENT_SUCCESS: {
      const accidentDetails = { ...state.accidentDetails } as Accident

      const postCommentSuccessAction = action as AccidentPostCommentSuccessAction
      if (accidentDetails && accidentDetails.id === parseInt(postCommentSuccessAction.payload.accidentId, 10)) {
        accidentDetails.comments = [ ...accidentDetails.comments, postCommentSuccessAction.payload.comment ]
      }

      return {
        ...state,
        accidentDetails,
        isPostingComment: false
      }
    }

    case actionTypes.ACCIDENT_POST_COMMENT_FAILED:
      return {
        ...state,
        isPostingComment: false
      }

    case actionTypes.ACCIDENT_FILTER_PARAMS_RESET: {
      const resetAction = action as AccidentFilterParamsResetAction

      const filterParams = resetAction.payload.filterParams
      return {
        ...state,
        filterParams: {
          ...initialFilterParams,
          ...(filterParams || {})
        }
      }
    }
    case actionTypes.ACCIDENT_FILTER_PARAMS_UPDATED: {
      const filterParams = {
        ...state.filterParams,
        ...(action as AccidentFilterChangedAction).payload.params
      }

      return {
        ...state,
        filterParams
      }
    }

    case actionTypes.ACCIDENT_HISTOGRAM_FETCH_SUCCESS:
      return {
        ...state,
        histogramData: (action as AccidentHistogramFetchSucceeded).payload.data
      }

    case actionTypes.ACCIDENTS_SUMMARY_FETCH_SUCCESS: {
      const summaryData = (action as AccidentSummaryFetchSucceeded).payload.data
      summaryData.countByType = orderBy(summaryData.countByType, ['count'], ['desc'])

      return {
        ...state,
        summaryData,
        isFetchingSummary: false
      }
    }

    case actionTypes.ACCIDENTS_PENDING_STATUS_COUNT_FETCH_SUCCESS: {
      const pendingStatusCountData = (action as AccidentPendingStatusCountFetchSucceeded).payload.data
      return {
        ...state,
        pendingStatusData: pendingStatusCountData
      }
    }

    case actionTypes.ACCIDENTS_FETCH:
      return {
        ...state,
        accidentDetails: undefined
      }

    case actionTypes.ACCIDENTS_FETCH_SUCCESS:
      return {
        ...state,
        accidentDetails: (action as AccidentsFetchAccidentSuccessAction).payload.accident
      }

    case actionTypes.ACCIDENTS_FETCH_FAILED:
      return {
        ...state,
        accidentDetails: undefined
      }

    case actionTypes.ACCIDENTS_FETCH_CLAIM:
      return {
        ...state,
        claimDetails: undefined
      }

    case actionTypes.ACCIDENTS_FETCH_CLAIM_SUCCESS:
      return {
        ...state,
        claimDetails: (action as AccidentsFetchClaimSuccessAction).payload.claim
      }

    case actionTypes.ACCIDENT_FETCH_CLAIM_AREAS_LIST_SUCCESS:
      return {
        ...state,
        claimAreas: (action as AccidentsFetchClaimAreasSuccessAction).payload.claimAreas
      }

    case actionTypes.ACCIDENT_REMOVE:
      return { ...state, isDeletingAccident: true }

    case actionTypes.ACCIDENT_REMOVE_SUCCESS:
      return { ...state, isDeletingAccident: false }

    case actionTypes.ACCIDENT_REMOVE_FAILED:
      return { ...state, isDeletingAccident: false }

    case actionTypes.ACCIDENT_SET_MANAGER_APPROVAL_SUCCESS: {
      const accidentSetManagerApprovalAction = action as AccidentSetManagerApprovalSuccessAction
      return { ...state, accidentDetails: accidentSetManagerApprovalAction.payload.accident }
    }

    case actionTypes.ACCIDENT_FETCH_FILES:
      return {
        ...state,
        isFetchingFiles: true,
        fetchFilesError: undefined
      }

    case actionTypes.ACCIDENT_FETCH_FILES_FAILED: {
      const accidentFilesErrorAction = action as AccidentFetchFilesErrorAction

      return {
        ...state,
        isFetchingFiles: false,
        fetchFilesError: accidentFilesErrorAction.payload.errorMessage
      }
    }

    case actionTypes.ACCIDENT_FETCH_FILES_SUCCESS: {
      const accidentFilesAction = action as AccidentFetchFilesSuccessAction

      const files = accidentFilesAction.payload.files
      const accidentId = accidentFilesAction.payload.accidentId

      const accidents: Accident[] = [ ...get(state, 'accidents', []) ]
      const accidentIndex = findIndex(accidents, { id: accidentId })

      if (accidentIndex >= 0) {
        const accident = accidents[accidentIndex]
        accident.files = files
        accidents.splice(accidentIndex, 1, accident)
      }

      const newState = { ...state, isFetchingFiles: false }

      if (state.currentAccident && state.currentAccident.id === accidentId) {
        const currentAccident = { ...state.currentAccident } as Accident

        currentAccident.files = files
        newState.currentAccident = currentAccident
      }

      if (state.accidentDetails && state.accidentDetails.id === accidentId) {
        const accidentDetails = { ...state.accidentDetails } as Accident

        accidentDetails.files = files
        newState.accidentDetails = accidentDetails
      }

      newState.fetchFilesError = undefined

      return newState
    }

    case actionTypes.ACCIDENT_FETCH_OPEN_CLAIMS:
      return { ...state, openClaims: undefined, isFetchingOpenClaims: true }

    case actionTypes.ACCIDENT_FETCH_OPEN_CLAIMS_FAILED:
      return { ...state, openClaims: undefined, isFetchingOpenClaims: false }

    case actionTypes.ACCIDENT_FETCH_OPEN_CLAIMS_SUCCESS: {
      const openClaimsAction = action as AccidentsFetchOpenClaimsSuccessAction

      return { ...state, isFetchingOpenClaims: false, openClaims: openClaimsAction.payload.claims }
    }

    case actionTypes.ACCIDENT_UPLOAD_FILE:
      return { ...state, isUploadingFile: true }

    case actionTypes.ACCIDENT_UPLOAD_FILE_SUCCESS:
      return { ...state, isUploadingFile: false }

    case actionTypes.ACCIDENT_UPLOAD_FILE_FAILED:
      return { ...state, isUploadingFile: false }

    case actionTypes.ACCIDENTS_SEARCH_CLAIMS:
      return { ...state, isSearchingClaims: true, claimsSearchError: undefined }

    case actionTypes.ACCIDENTS_SEARCH_CLAIMS_FAILED: {
      const searchClaimsErrorAction = action as AccidentsSearchClaimsErrorAction
      return {
        ...state,
        isSearchingClaims: false,
        claimSearchResults: undefined,
        claimsSearchError: searchClaimsErrorAction.payload.errorMessage
      }
    }

    case actionTypes.ACCIDENTS_SEARCH_CLAIMS_SUCCESS: {
      const searchClaimsAction = action as AccidentsSearchClaimsSuccessAction
      return {
        ...state,
        isSearchingClaims: false,
        claimsSearchError: undefined,
        claimSearchResults: searchClaimsAction.payload.claims
      }
    }

    case actionTypes.ACCIDENTS_RESET_CLAIMS_SEARCH:
      return {
        ...state,
        isSearchingClaims: false,
        claimSearchResults: undefined
      }

    case actionTypes.ACCIDENTS_REMOVE_CLAIM:
      return { ...state, isDeletingClaim: true }

    case actionTypes.ACCIDENTS_REMOVE_CLAIM_SUCCESS:
      return { ...state, isDeletingClaim: false }

    case actionTypes.ACCIDENTS_REMOVE_CLAIM_FAILED:
      return { ...state, isDeletingClaim: false }

    default:
     return state
  }
}



// WEBPACK FOOTER //
// ./src/store/accidents/reducer.ts